<script lang="ts">
	import { env } from '$env/dynamic/public';
	import {
		Bot,
		Boxes,
		Container,
		Database,
		Eye,
		GitBranch,
		Image,
		Key,
		MessageSquare,
		Server
	} from '@lucide/svelte';
	import { mode } from 'mode-watcher';
	import type { Component } from 'svelte';

	type Item = { name: string; domain: string; Icon: Component<any> };

	const items: Item[] = [
		{ name: 'Docker', domain: 'docker.com', Icon: Container },
		{ name: 'Kubernetes', domain: 'kubernetes.io', Icon: Boxes },
		{ name: 'Claude', domain: 'anthropic.com', Icon: Bot },
		{ name: 'GitHub', domain: 'github.com', Icon: GitBranch },
		{ name: 'OpenAI', domain: 'openai.com', Icon: Bot },
		{ name: 'Postgres', domain: 'postgresql.org', Icon: Database },
		{ name: 'Slack', domain: 'slack.com', Icon: MessageSquare },
		{ name: 'AWS', domain: 'aws.amazon.com', Icon: Server },
		{ name: 'Vault', domain: 'vaultproject.io', Icon: Key },
		{ name: 'OpenTelemetry', domain: 'opentelemetry.io', Icon: Eye },
		{ name: 'Logo.dev', domain: 'logo.dev', Icon: Image }
	];

	const hasLogoDevToken = !!env.PUBLIC_LOGO_DEV_TOKEN;

	let failed = $state<Record<string, boolean>>({});

	// logo.dev's `theme` param returns light-on-transparent monochrome logos
	// for `dark` and dark-on-transparent for `light`. Mismatch makes the GitHub
	// octocat, OpenAI mark, etc. disappear on the wrong background — so we mirror
	// the current site mode (defaulting to dark when undefined during SSR).
	const logoTheme = $derived(mode.current === 'light' ? 'light' : 'dark');

	function logoUrl(domain: string, theme: 'light' | 'dark'): string {
		if (!env.PUBLIC_LOGO_DEV_TOKEN) return '';
		const params = new URLSearchParams({
			token: env.PUBLIC_LOGO_DEV_TOKEN,
			format: 'png',
			theme,
			size: '128',
			retina: 'true'
		});
		return `https://img.logo.dev/${domain}?${params.toString()}`;
	}

	// Clear per-logo failure flags when the theme flips so a logo that 404'd on
	// one theme gets retried on the other (different URL = different cache key).
	$effect(() => {
		void logoTheme;
		failed = {};
	});
</script>

<section class="bg-surface-sunken border-border relative border-y py-10">
	<div class="mx-auto flex max-w-7xl items-center gap-8 px-8 pb-6">
		<div class="kicker">
			<span class="kicker-rule"></span>
			<span>Integrations</span>
		</div>
		<div class="bg-border h-px flex-1"></div>
		<div class="text-soft-gray hidden text-[13px] sm:block">
			Integrate with the tools you already use.
		</div>
	</div>

	<div class="marquee">
		<div class="marquee-track">
			<ul class="marquee-list" aria-label="Integrations">
				{#each items as it (it.name)}
					{@const IconCmp = it.Icon}
					<li class="marquee-item">
						{#if !hasLogoDevToken || failed[it.name]}
							<IconCmp class="size-4.5" />
						{:else}
							<img
								src={logoUrl(it.domain, logoTheme)}
								alt="{it.name} logo"
								class="h-7 w-7 bg-transparent object-contain"
								loading="lazy"
								onerror={() => (failed[it.name] = true)}
							/>
						{/if}
						{it.name}
					</li>
				{/each}
			</ul>
			<ul class="marquee-list" aria-hidden="true">
				{#each items as it (it.name)}
					{@const IconCmp = it.Icon}
					<li class="marquee-item">
						{#if !hasLogoDevToken || failed[it.name]}
							<IconCmp class="size-4.5" />
						{:else}
							<img
								src={logoUrl(it.domain, logoTheme)}
								alt="{it.name} logo"
								class="h-7 w-7 bg-transparent object-contain"
								loading="lazy"
								onerror={() => (failed[it.name] = true)}
							/>
						{/if}
						{it.name}
					</li>
				{/each}
			</ul>
		</div>
	</div>

</section>

<style>
	.marquee {
		overflow: hidden;
		-webkit-mask-image: linear-gradient(
			to right,
			transparent 0%,
			#000 10%,
			#000 90%,
			transparent 100%
		);
		mask-image: linear-gradient(to right, transparent 0%, #000 10%, #000 90%, transparent 100%);
	}
	.marquee-track {
		display: flex;
		align-items: center;
		width: max-content;
		animation: scroll-x 90s linear infinite;
		will-change: transform;
	}
	@keyframes scroll-x {
		from {
			transform: translateX(0);
		}
		to {
			transform: translateX(-50%);
		}
	}
	.marquee-list {
		display: flex;
		gap: 72px;
		align-items: center;
		padding: 0 72px 0 0;
		margin: 0;
		list-style: none;
	}
	.marquee-item {
		font-size: 20px;
		font-weight: 500;
		letter-spacing: -0.02em;
		color: var(--color-soft-gray);
		opacity: 0.75;
		display: inline-flex;
		align-items: center;
		gap: 12px;
		white-space: nowrap;
	}
	@media (prefers-reduced-motion: reduce) {
		.marquee {
			overflow-x: auto;
		}
		.marquee-track {
			animation: none;
			transform: none;
		}
	}
</style>
